import useSurveyState from "@hooks/useSurveyState";
import {
  useCreateSurveyNotifierMutation,
  useDeleteSurveyNotifierMutation,
  useGetSurveyNotifiersQuery,
  useUpdateSurveyNotifierMutation,
} from "@services/survey-notifications";
import { useGetAllUsersQuery } from "@services/user";
import SkeletonLoader from "@shared/components/SkeletonLoader";
import type { AllUser } from "@shared/types/user.types";
import type { SurveyNotifier } from "@survey-types/survey.core.types";
import { useEffect, useState } from "react";
import NotificationList from "./NotificationList";
import UserSearchDropdown from "./UserSearchDropdown";

const ShareNotificationsSection = () => {
  const { survey } = useSurveyState();
  const [currentEmail, setCurrentEmail] = useState("");
  const [notifiers, setNotifiers] = useState<SurveyNotifier[]>([]);
  const emailStatusMessage =
    "Your one-time email notification option is no longer available since Shortlisting has already begun for this Survey.";

  // Fetch notifiers from API
  const { data: apiNotifiers, isLoading } = useGetSurveyNotifiersQuery(
    survey?.id || "",
    {
      skip: !survey?.id,
    },
  );

  // Fetch all users
  const { data: allUsers = [] } = useGetAllUsersQuery();

  // Create notifier mutation
  const [createNotifier] = useCreateSurveyNotifierMutation();

  // Delete notifier mutation
  const [deleteNotifier] = useDeleteSurveyNotifierMutation();

  // Update notifier mutation
  const [updateNotifier] = useUpdateSurveyNotifierMutation();

  // Load from localStorage on mount
  useEffect(() => {
    if (survey?.id) {
      const storageKey = `survey_notifiers_${survey.id}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedNotifiers = JSON.parse(stored);
        setNotifiers(parsedNotifiers);
      }
    }
  }, [survey?.id]);

  // Update local state and localStorage when API data is loaded
  useEffect(() => {
    if (apiNotifiers && survey?.id) {
      setNotifiers(apiNotifiers);
      const storageKey = `survey_notifiers_${survey.id}`;
      localStorage.setItem(storageKey, JSON.stringify(apiNotifiers));
    }
  }, [apiNotifiers, survey?.id]);

  // Update localStorage whenever notifiers change
  const updateLocalStorage = (updatedNotifiers: SurveyNotifier[]) => {
    if (survey?.id) {
      const storageKey = `survey_notifiers_${survey.id}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedNotifiers));
    }
  };

  const handleUserSelect = async (user: AllUser) => {
    const isEmailAlreadyExists = notifiers.some(
      (notifier) => notifier.email === user.email,
    );

    if (user.email && !isEmailAlreadyExists && survey?.id) {
      // Optimistic update - add to local state immediately
      const optimisticNotifier: SurveyNotifier = {
        notificationId: `temp_${Date.now()}`, // Temporary ID
        email: user.email,
        isShortlistEnabled: true,
        isCommentsEnabled: true,
        surveyId: survey.id,
        createdBy: "current-user",
        createdOn: new Date().toISOString(),
        updatedBy: "current-user",
        updatedOn: new Date().toISOString(),
      };

      const updatedNotifiers = [...notifiers, optimisticNotifier];
      setNotifiers(updatedNotifiers);
      updateLocalStorage(updatedNotifiers);
      setCurrentEmail("");

      const result = await createNotifier({
        email: user.email,
        surveyId: survey.id,
      }).unwrap();

      // Replace optimistic notifier with real one
      const finalNotifiers = updatedNotifiers.map((n) =>
        n.notificationId === optimisticNotifier.notificationId ? result : n,
      );
      setNotifiers(finalNotifiers);
      updateLocalStorage(finalNotifiers);
    }
  };

  const handleUpdateNotifier = async (
    notifierId: string,
    isShortlistEnabled: boolean,
  ) => {
    // Optimistic update
    const updatedNotifiers = notifiers.map((notifier) =>
      notifier.notificationId === notifierId
        ? {
            ...notifier,
            isShortlistEnabled,
            updatedBy: "current-user",
            updatedOn: new Date().toISOString(),
          }
        : notifier,
    );
    setNotifiers(updatedNotifiers);
    updateLocalStorage(updatedNotifiers);

    await updateNotifier({
      notifierId,
      updates: { isShortlistEnabled },
    }).unwrap();
  };

  const handleRemoveNotifier = async (email: string) => {
    const notifierToDelete = notifiers.find((n) => n.email === email);

    if (notifierToDelete?.notificationId) {
      // Optimistic update - remove from local state immediately
      const updatedNotifiers = notifiers.filter((n) => n.email !== email);
      setNotifiers(updatedNotifiers);
      updateLocalStorage(updatedNotifiers);

      await deleteNotifier(notifierToDelete.notificationId).unwrap();
    }
  };

  return (
    <div>
      {survey?.emailNotificationStatus === "SENT" ? (
        <>
          <p className="text-sm mb-3">{emailStatusMessage}</p>
        </>
      ) : (
        <div>
          <p className="text-sm mb-3">
            Enter email addresses for Site IQ user(s) to receive an email
            notification once your client starts to shortlist or add comments.
          </p>

          <UserSearchDropdown
            users={allUsers}
            searchValue={currentEmail}
            onSearchChange={setCurrentEmail}
            onUserSelect={handleUserSelect}
          />

          {notifiers.length > 0 && (
            <div className="text-sm mb-2 mt-4">Notification Recipients</div>
          )}

          {isLoading ? (
            <SkeletonLoader count={3} className="h-10 mb-2" />
          ) : (
            <NotificationList
              notifiers={notifiers}
              onUpdateNotifier={handleUpdateNotifier}
              onRemoveNotifier={handleRemoveNotifier}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ShareNotificationsSection;
